import { forwardRef, ReactNode } from "react";
import styled, { css, CSSObject } from "styled-components";
import { colorV2, cssTemplate, typographyV2 } from "../../constants/styles";

interface ButtonV2Props {
  label: ReactNode;
  onClick?: () => void;
  size?: "S" | "L";
  theme?: "main" | "sub";
  align?: "left" | "center" | "right";
  className?: string;
  style?: CSSObject;
}

/**
 * @default size = "L"
 * @default theme = "main"
 * @default align = "center"
 */
const ButtonV2 = forwardRef<HTMLButtonElement, ButtonV2Props>(
  (
    {
      label,
      onClick,
      size = "L",
      theme = "main",
      align = "center",
      className,
      style,
    },
    ref
  ) => {
    return (
      <Button
        ref={ref}
        size={size}
        buttonTheme={theme}
        onClick={onClick}
        align={align}
        className={className}
        style={style}
      >
        {label}
      </Button>
    );
  }
);

export default ButtonV2;

const Button = styled.button<{
  size: Exclude<ButtonV2Props["size"], undefined>; // Exclude - 타입 확장 고려하여 사용
  buttonTheme: Exclude<ButtonV2Props["theme"], undefined>;
  align: Exclude<ButtonV2Props["align"], undefined>;
}>`
  all: unset;
  ${cssTemplate.flexCenter}
  cursor: pointer;
  border-radius: 16px;
  transition: all 0.3s;
  ${({ align }) => alignStyles[align]}
  ${({ size }) => sizeStyles[size]}
  ${({ buttonTheme }) => themeStyles[buttonTheme]}
`;

const alignStyles = {
  left: css`
    justify-content: flex-start;
  `,
  center: css`
    justify-content: center;
  `,
  right: css`
    justify-content: flex-end;
  `,
};

const sizeStyles = {
  L: css`
    padding: 20px 42px;
    ${typographyV2.BODY_4_M}
  `,
  S: css`
    padding: 12px 32px;
    ${typographyV2.BODY_8_M}
  `,
};

const themeStyles = {
  main: css`
    background-color: ${colorV2.PRIMARY_05};
    color: ${colorV2.GRAY_01};
    &:hover {
      background-color: ${colorV2.PRIMARY_07};
    }
  `,
  sub: css`
    background-color: ${colorV2.GRAY_01};
    color: ${colorV2.GRAY_10};
    &:hover {
      background-color: ${colorV2.GRAY_03};
    }
  `,
};
